// Notices when a newer build has been deployed than the one this tab is
// running. The dashboard is a single page: switching tabs inside it never
// loads new code, so an office tab left open across a deploy keeps drawing
// the old screens (and, after a data change, shows dashes where the new
// build has columns). The check compares the content-hashed main bundle
// named by the live index.html with the one this page loaded.
import { useEffect, useState } from "react";

const BUNDLE_RE = /static\/js\/main\.([a-z0-9]+)\.js/;

export function runningBundleHash() {
  const src = Array.from(document.scripts).map((s) => s.src || "").find((s) => BUNDLE_RE.test(s));
  const m = src ? src.match(BUNDLE_RE) : null;
  return m ? m[1] : null;
}

export async function deployedBundleHash() {
  // "updatecheck" makes the service worker pass this straight to the network
  // (and never store it); the timestamp defeats any HTTP cache in between.
  const res = await fetch(`/index.html?updatecheck=${Date.now()}`, { cache: "no-store", credentials: "same-origin" });
  if (!res.ok) return null;
  const html = await res.text();
  const m = html.match(BUNDLE_RE);
  return m ? m[1] : null;
}

// True once the deployed build differs from the running one. Polls every
// `intervalMs` and whenever the tab regains focus; never flips back.
export function useUpdateAvailable(intervalMs = 60000) {
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return undefined;
    const running = runningBundleHash();
    if (!running) return undefined;
    let stopped = false;
    const check = async () => {
      try {
        const deployed = await deployedBundleHash();
        if (!stopped && deployed && deployed !== running) setAvailable(true);
      } catch (e) {
        /* offline or mid-deploy; try again next time */
      }
    };
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    const timer = setInterval(check, intervalMs);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", check);
    const first = setTimeout(check, 15000);
    return () => {
      stopped = true;
      clearInterval(timer);
      clearTimeout(first);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", check);
    };
  }, [intervalMs]);
  return available;
}

export function reloadForUpdate() {
  window.location.reload();
}
